import { Role, Permission } from '@/types/Role';
import { getRolePermissions } from '@/config/roleConfig';
import { includesIgnoreCase } from './arrayUtils';

const matchesRoutePattern = (routePattern: string, actualRoute: string): boolean => {
  // Convert route pattern to regex
  const patternRegex = routePattern
    .replace(/:[^/]+/g, '[^/]+') // Replace :param with regex pattern
    .replace(/\//g, '\\/'); // Escape forward slashes
  
  const regex = new RegExp(`^${patternRegex}$`);
  return regex.test(actualRoute);
};

export const hasRole = (userRoles: Role[], requiredRole: Role): boolean => {
  return includesIgnoreCase(userRoles, requiredRole);
};

export const hasAnyRole = (userRoles: Role[], requiredRoles: Role[]): boolean => {
  return requiredRoles.some(role => includesIgnoreCase(userRoles, role));
};

export const hasAllRoles = (userRoles: Role[], requiredRoles: Role[]): boolean => {
  return requiredRoles.every(role => includesIgnoreCase(userRoles, role));
};

export const hasPermission = (userRoles: Role[], resource: string, action: string): boolean => {
  return userRoles.some(role => {
    const rolePerms = getRolePermissions(role);
    if (!rolePerms) return false;
    
    return rolePerms.permissions.some(permission => 
      permission.resource === resource && permission.action === action
    );
  });
};

export const canAccessRoute = (userRoles: Role[], route: string): boolean => {
  return userRoles.some(role => {
    const rolePerms = getRolePermissions(role);
    if (!rolePerms) return false;
    
    return rolePerms.allowedRoutes.some(allowedRoute => 
      matchesRoutePattern(allowedRoute, route)
    );
  });
};

export const getUserPermissions = (userRoles: Role[]): Permission[] => {
  const allPermissions = new Map<string, Permission>();
  
  userRoles.forEach(role => {
    const rolePerms = getRolePermissions(role);
    if (rolePerms) {
      rolePerms.permissions.forEach(permission => {
        const key = `${permission.resource}:${permission.action}`;
        allPermissions.set(key, permission);
      });
    }
  });
  
  return Array.from(allPermissions.values());
}; 